import React, { useMemo, useEffect } from "react";
import PropTypes from "prop-types";

import { useLazyQuery } from "react-apollo";

import { Column, Field, Control, Label, Input, Box, Title, Help } from "rbx";
import {
  SystemCodeSelect,
  EmployeeSelect,
  VehicleSelect,
  ItemCategorySelect,
  BooleanInput,
  CompanyLocationSelect,
  VendorSelect
} from "../../../../components/inputs";
import { ALL_INSPECTION_MASTER_QUERY } from "../../../../graphql/InspectionMaster/InspectionMaster.graphql";

import { useSystemCodes } from "../../../../utils/hooks";
import { useOdometerRequired } from "../../../../hooks";

function MainInformation({
  inputs,
  editing,
  onChange,
  vehicleStatus,
  companySettings,
  VSSystemCode,
  isPosted
}) {
  const [getInspectionMasters, { data: inspectionMastersData }] = useLazyQuery(
    ALL_INSPECTION_MASTER_QUERY
  );

  const { data: tractorVehicleTypeSystemCode } = useSystemCodes({
    Category: "Fleet",
    CodeName: "VehicleType",
    CodeDescription: "Tractor"
  });

  const { data: inspectionServiceSystemCode } = useSystemCodes({
    Category: "Fleet",
    CodeName: "WOType",
    CodeDescription: "Inspection Service"
  });

  const odometerRequired = useOdometerRequired(
    inputs.WOTypeRecIdRef,
    inputs.ItemCatgRecIdRef
  );

  useEffect(() => {
    if (
      Array.isArray(inspectionServiceSystemCode) &&
      (inputs?.VehicleTypeRecIdRef || inputs?.Vehicle?.VehicleTypeRecIdRef) &&
      parseInt(inspectionServiceSystemCode[0]?.RecId) ===
        parseInt(inputs?.WOTypeRecIdRef)
    ) {
      getInspectionMasters({
        variables: {
          where: {
            VehicleTypeRecIdRef: parseInt(
              inputs?.VehicleTypeRecIdRef ||
                inputs.Vehicle?.VehicleTypeRecIdRef,
              10
            )
          }
        }
      });
    }
  }, [getInspectionMasters, inputs, inspectionServiceSystemCode]);

  const isTractor = useMemo(
    () =>
      Array.isArray(tractorVehicleTypeSystemCode) &&
      (inputs?.VehicleTypeRecIdRef || inputs?.Vehicle?.VehicleTypeRecIdRef) &&
      tractorVehicleTypeSystemCode[0].RecId ===
        parseInt(
          inputs?.VehicleTypeRecIdRef || inputs.Vehicle.VehicleTypeRecIdRef,
          10
        ),
    [tractorVehicleTypeSystemCode, inputs]
  );

  const showOdometerHelp = !!(
    (isTractor || odometerRequired) &&
    inputs.Odometer &&
    inputs?.Vehicle?.Odometer &&
    parseInt(inputs.Odometer, 10) < parseInt(inputs.Vehicle.Odometer, 10)
  );

  const getItemCategoryWhere = useMemo(() => {
    if (
      Array.isArray(inspectionServiceSystemCode) &&
      inspectionServiceSystemCode[0]?.RecId ===
        parseInt(inputs?.WOTypeRecIdRef, 10)
    ) {
      if (
        (inputs?.VehicleTypeRecIdRef || inputs?.Vehicle?.VehicleTypeRecIdRef) &&
        inspectionMastersData?.InspectionMasters
      ) {
        return {
          ItemCatgTypeRecIdRef: Number(inputs?.WOTypeRecIdRef),
          InspectionCdRecIdRef_in: inspectionMastersData.InspectionMasters.map(
            inspectionMaster => inspectionMaster.InspecCdRecIdRef
          )
        };
      }
      return { ItemCatgTypeRecIdRef: 0 };
    }
    return { ItemCatgTypeRecIdRef: Number(inputs?.WOTypeRecIdRef) };
  }, [inputs, inspectionMastersData, inspectionServiceSystemCode]);

  return (
    <React.Fragment>
      <Column.Group>
        <Column>
          <Box>
            <Title size={4}>Vehicle Information</Title>
            <Field>
              <Column.Group>
                <Column size={2}>
                  <SystemCodeSelect
                    disabled={!editing}
                    icon="truck"
                    label="Vehicle Type"
                    name="VehicleTypeRecIdRef"
                    readOnly={!editing}
                    value={
                      inputs.VehicleTypeRecIdRef ||
                      inputs.Vehicle?.VehicleTypeRecIdRef
                    }
                    where={{
                      Category: "Fleet",
                      CodeName: "VehicleType",
                      CodeDescription_in: ["Tractor", "Trailer"]
                    }}
                    onChange={onChange}
                  />
                </Column>
                <Column>
                  <VehicleSelect
                    firstOptionEnabled
                    orderByNumber
                    required
                    showHelp
                    Vehicle={inputs.Vehicle}
                    disabled={!editing}
                    icon="globe"
                    label="Vehicle Number"
                    name="VehicleRecIdRef"
                    value={inputs.VehicleRecIdRef}
                    vehicleStatus={
                      inputs.Status?.CodeDescription !== "Posted"
                        ? vehicleStatus
                        : null
                    }
                    vehicleType={inputs.VehicleTypeRecIdRef}
                    onChange={onChange}
                  />
                </Column>
                <Column>
                  <VehicleSelect
                    firstOptionEnabled
                    required
                    showHelp
                    Vehicle={inputs.Vehicle}
                    disabled={!editing}
                    icon="globe"
                    label="Vehicle Description"
                    name="VehicleRecIdRef"
                    value={inputs.VehicleRecIdRef}
                    vehicleStatus={
                      inputs.Status?.CodeDescription !== "Posted"
                        ? vehicleStatus
                        : null
                    }
                    vehicleType={inputs.VehicleTypeRecIdRef}
                    onChange={onChange}
                  />
                </Column>
                <Column>
                  <Control>
                    <Label>Odometer</Label>
                    <Input
                      autoComplete="new"
                      name="Odometer"
                      readOnly={!editing}
                      required={!isPosted && (isTractor || odometerRequired)}
                      type="number"
                      value={inputs.Odometer}
                      onChange={onChange}
                    />
                    {!isPosted && showOdometerHelp && (
                      <Help color="danger">
                        Odometer must be greater than current odometer reading.
                      </Help>
                    )}
                  </Control>
                </Column>
              </Column.Group>
            </Field>
            {inputs.Vehicle && (
              <Field>
                <Column.Group>
                  <Column size={1}>
                    <Control>
                      <Label>Type</Label>
                      <Input
                        readOnly
                        name="WODate"
                        type="text"
                        value={inputs.Vehicle.VehicleType?.CodeDescription}
                      />
                    </Control>
                  </Column>
                  <Column size={1}>
                    <Control>
                      <Label>Year</Label>
                      <Input
                        readOnly
                        name="WODate"
                        type="text"
                        value={inputs.Vehicle.Year}
                      />
                    </Control>
                  </Column>
                  <Column size={2}>
                    <Control>
                      <Label>Make</Label>
                      <Input
                        readOnly
                        name="WODate"
                        type="text"
                        value={inputs.Vehicle.Make}
                      />
                    </Control>
                  </Column>
                  <Column size={2}>
                    <Control>
                      <Label>Model</Label>
                      <Input
                        readOnly
                        name="WODate"
                        type="text"
                        value={inputs.Vehicle.Model}
                      />
                    </Control>
                  </Column>
                  <Column size={2}>
                    <Control>
                      <Label>Serial #</Label>
                      <Input
                        readOnly
                        name="WODate"
                        type="text"
                        value={inputs.Vehicle.SerialNumber}
                      />
                    </Control>
                  </Column>
                  <Column size={2}>
                    <Control>
                      <Label>Current Odometer</Label>
                      <Input
                        readOnly
                        name="WODate"
                        type="text"
                        value={inputs.Vehicle.Odometer}
                      />
                    </Control>
                  </Column>
                  <Column size={2}>
                    <Control>
                      <Label>Current Status</Label>
                      <Input
                        readOnly
                        name="WODate"
                        type="text"
                        value={inputs.Vehicle.Status?.CodeDescription}
                      />
                    </Control>
                  </Column>
                </Column.Group>
              </Field>
            )}
          </Box>
          <Box>
            <Title size={4}>Work Order Information</Title>
            <Field>
              <Column.Group>
                <Column>
                  <SystemCodeSelect
                    required
                    label="Work Order Type"
                    lookup={{ Code: "WOType", Category: "Fleet" }}
                    name="WOTypeRecIdRef"
                    readOnly={!editing}
                    value={Number(inputs.WOTypeRecIdRef)}
                    onChange={onChange}
                  />
                </Column>
                {VSSystemCode &&
                  parseInt(VSSystemCode.RecId) ===
                    parseInt(inputs.WOTypeRecIdRef) && (
                    <Column>
                      <Control>
                        <VendorSelect
                          orderByNumber
                          showHelp
                          disabled={!editing}
                          label="Vendor"
                          name="VendorRecIdRef"
                          value={inputs.VendorRecIdRef}
                          onChange={onChange}
                        />
                      </Control>
                    </Column>
                  )}
                <Column>
                  <ItemCategorySelect
                    required
                    icon="globe"
                    label="Item Category"
                    name="ItemCatgRecIdRef"
                    readOnly={!editing}
                    value={inputs?.ItemCatgRecIdRef || 0}
                    where={getItemCategoryWhere}
                    onChange={onChange}
                  />
                </Column>
                <Column>
                  <EmployeeSelect
                    disableInActive
                    disabled={!editing}
                    icon="user"
                    label="Mechanic"
                    name="MechanicRecIdRef"
                    readOnly={!editing}
                    required={inputs.Status?.CodeDescription === "Completed"}
                    value={Number(inputs.MechanicRecIdRef)}
                    variables={{
                      where: { MechanicFlag: true },
                      orderByMulti: ["LastName_ASC"],
                      orderBy: "LastName_ASC"
                    }}
                    onChange={onChange}
                  />
                </Column>
              </Column.Group>
            </Field>
            <Field>
              <Column.Group>
                <Column>
                  <CompanyLocationSelect
                    required
                    icon="globe"
                    label="Company Location"
                    name="LocRecIdRef"
                    readOnly={!editing}
                    value={inputs.LocRecIdRef}
                    variables={{ Active: true }}
                    onChange={onChange}
                  />
                </Column>
                <Column>
                  <Control>
                    <Label>Scheduled Date</Label>
                    <Input
                      disabled={!editing}
                      name="WODate"
                      type="date"
                      value={inputs.WODate}
                      onChange={onChange}
                    />
                  </Control>
                </Column>
                <Column>
                  <Control>
                    <Label>Completed Date</Label>
                    <Input
                      disabled={!editing}
                      name="CompletedDate"
                      type="date"
                      value={inputs.CompletedDate}
                      onChange={onChange}
                    />
                  </Control>
                </Column>
                {companySettings.AllowMaintenanceBilling && (
                  <Column>
                    <Control>
                      <Label>Billable</Label>
                      <BooleanInput
                        disabled={
                          !companySettings.AllowMaintenanceBilling || !editing
                        }
                        name="Billable"
                        value={inputs.Billable}
                        onChange={onChange}
                      />
                    </Control>
                  </Column>
                )}
              </Column.Group>
            </Field>
            {inputs.PostedBy && inputs.PostedDt && (
              <Field>
                <Column.Group>
                  <Column>
                    <Control>
                      <Label>Posted By</Label>
                      <Input
                        disabled={!editing}
                        name="PostedBy"
                        type="text"
                        value={`${inputs.PostedBy.FirstName} ${inputs.PostedBy.LastName}`}
                      />
                    </Control>
                  </Column>
                  <Column>
                    <Control>
                      <Label>Posted Date</Label>
                      <Input
                        disabled={!editing}
                        name="PostedDt"
                        type="date"
                        value={inputs.PostedDt}
                      />
                    </Control>
                  </Column>
                </Column.Group>
              </Field>
            )}
            <Field>
              <Column.Group>
                <Column>
                  <Control expanded>
                    <Label>Notes</Label>
                    <Input
                      autoComplete="new"
                      disabled={!editing}
                      name="Notes"
                      type="text"
                      value={inputs.Notes}
                      onChange={onChange}
                    />
                  </Control>
                </Column>
              </Column.Group>
            </Field>
          </Box>
        </Column>
      </Column.Group>
    </React.Fragment>
  );
}

MainInformation.propTypes = {
  onChange: PropTypes.func,
  editing: PropTypes.bool,
  inputs: PropTypes.object,
  companySettings: PropTypes.object,
  vehicleStatus: PropTypes.array,
  VSSystemCode: PropTypes.object,
  isPosted: PropTypes.bool
};
MainInformation.defaultProps = {
  isPosted: false
};
export default MainInformation;
